import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { Dataset, DatasetSubtype, Document } from '@app/modules/core/store/models';
import { Layer } from '@app/modules/cop/store/cop/models/layer.model';
import { IconComponent, IconProperties } from '@app/shared/components/icon';
import { StripHtmlPipe } from '@app/shared/pipes';
import { DataApiService } from '@app/modules/cop/services/data/data-api.service';
import { AlertUtilsService, ImpactedAssetsService } from '@app/features/operating-picture/services';
import { TimerService } from '@app/core/services';
import { map } from 'rxjs/operators';
import { AsyncPipe, NgClass } from '@angular/common';

export interface AlertCardConfig {
  uId: string;
  documentPartial: Partial<Document>;
  layer: Layer;
  dataset: Dataset;
}

interface ImpactedAssetsDetails {
  severityClass: string;
  distance: number;
  distanceUnit: string;
  counts: {
    people: number;
    facilities: number;
    other: number;
  };
}

@Component({
    selector: 'topo-alert-card',
    imports: [IconComponent, StripHtmlPipe, AsyncPipe, NgClass],
    templateUrl: './alert-card.component.html',
    styleUrl: './alert-card.component.scss'
})
export class AlertCardComponent {
  private dataApi = inject(DataApiService);
  private alertUtils = inject(AlertUtilsService);
  private impactedAssets = inject(ImpactedAssetsService);
  private timer = inject(TimerService);

  protected document = signal<Document>(null);
  protected impactedAssetsDetails = signal<ImpactedAssetsDetails>(null);

  public config = input<AlertCardConfig>();

  protected content = computed(() => {
    const document = this.document() ?? {};
    return document.email_subject || document.summary || document.title || document.name || document.content || document.display_name;
  });

  protected iconProps = computed<IconProperties>(() => {
    const { layer, dataset } = this.config();
    const document = this.document();
    const props = this.alertUtils.getIconProperties({ document, layer, dataset });

    return {
      ...props,
      style: {
        ...props.style,
        height: '3vh',
        width: '3vh',
        fontSize: `${dataset.subtype === DatasetSubtype.Weather ? 1.75 : 3}vh`
      }
    };
  });

  protected headerLabel = computed(() => {
    const { layer, dataset } = this.config();
    const document = this.document();

    const label = this.alertUtils.getHeaderLabel({ dataset, document, layer });

    return label;
  });

  protected showImpactedAssets = computed(() => {
    const { dataset } = this.config();

    return this.alertUtils.shouldDisplayImpactedAssets(dataset.subtype);
  });

  protected document$ = effect(async () => {
    const { dataset, uId } = this.config();

    const document = await this.dataApi
      .getDocument(dataset.uId, uId)
      .toPromise()
      .catch(err => {
        console.error(err);
        return null;
      });

    if (!!document) {
      this.document.set(document);

      const showImpactedAssets = this.showImpactedAssets();
      if (showImpactedAssets) {
        const { countsByLayer, distance, distanceUnit } = await this.impactedAssets
          .getImpactedAssetsForDocument({ dataset, document, unitDisplayOptions: { abbreviated: true } })
          .toPromise();

        const counts = countsByLayer.reduce(
          (acc, { count, datasetSubtype }) => {
            switch (datasetSubtype) {
              case DatasetSubtype.Person:
              case DatasetSubtype.TravelItinerary:
              case DatasetSubtype.HorizonPerson:
                acc.people += count;
                break;
              case DatasetSubtype.Facility:
                acc.facilities += count;
                break;
              default:
                acc.other += count;
                break;
            }

            return acc;
          },
          {
            people: 0,
            facilities: 0,
            other: 0
          } as ImpactedAssetsDetails['counts']
        );

        const severityClass = this.getSeverityClass(document);

        this.impactedAssetsDetails.set({
          severityClass,
          distance,
          distanceUnit,
          counts
        });
      } else {
        this.impactedAssetsDetails.set(null);
      }
    }
  });

  protected elapsedTime$ = this.timer.topOfMinute$.pipe(
    map(now => {
      const document = this.document();

      if (!document) {
        return;
      }

      const { dataset } = this.config();

      const updatedDate = this.alertUtils.getPostedDate(document, dataset.subtype, 'date_updated');
      return updatedDate.from(now);
    })
  );

  private getSeverityClass(document: Document): string {
    switch (document?.severity_number) {
      case 1:
        return 'is-minimal';
      case 2:
        return 'is-low';
      case 3:
        return 'is-moderate';
      case 4:
        return 'is-high';
      case 5:
        return 'is-extreme';
      default:
        return 'is-unknown';
    }
  }
}
